import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;

import {
  Clock,
  Hourglass,
  CheckCircle,
  XCircle,
  ForkKnife,
  Receipt,
} from '@phosphor-icons/react';
const STATUS_META = {
  new: { label: 'Order Received', icon: Clock, color: '#fbbf24' },
  confirmed: { label: 'Confirmed', icon: Clock, color: '#fbbf24' },
  preparing: { label: 'Preparing', icon: Hourglass, color: '#f97316' },
  ready: { label: 'Ready', icon: CheckCircle, color: '#22c55e' },
  completed: { label: 'Served', icon: CheckCircle, color: '#22c55e' },
  cancelled: { label: 'Cancelled', icon: XCircle, color: '#ef4444' },
};

/**
 * Public bookmarkable order status page. URL: /o/:orderNumber
 * No authentication required. Auto-refreshes every 5s.
 */
const OrderStatusPage = () => {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchOrder = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/public/orders/by-number/${orderNumber}`);
        if (!cancelled) setOrder(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? 'Order not found' : 'Could not load order');
      }
    };
    fetchOrder();
    const t = setInterval(fetchOrder, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [orderNumber]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950 text-white flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center rounded-2xl bg-white/5 border border-white/10 p-8" data-testid="order-status-error">
          <XCircle size={56} weight="duotone" className="mx-auto mb-4 text-red-400" />
          <h1 className="text-2xl font-bold mb-2">{error}</h1>
          <p className="text-white/60 text-sm">Double-check the order number on your receipt.</p>
          <p className="text-white/40 text-xs mt-4 font-mono">#{orderNumber}</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950 text-white flex items-center justify-center">
        <p className="text-white/50">Loading order…</p>
      </div>
    );
  }

  const s = STATUS_META[order.status] || STATUS_META.new;
  const Icon = s.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950 text-white p-6 flex flex-col items-center" data-testid="order-status-page">
      <div className="max-w-md w-full mt-10">
        {/* Outlet badge */}
        {order.outlet_name && (
          <p className="text-center text-orange-300 text-xs uppercase tracking-widest mb-1">
            {order.outlet_name}
          </p>
        )}
        <h1 className="text-center text-3xl font-extrabold mb-1">Order #{order.order_number}</h1>
        <p className="text-center text-white/50 text-sm mb-8 capitalize">
          {order.order_type?.replace('_', ' ')}
        </p>

        {/* Status core */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-6 text-center mb-4">
          <div
            className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: `${s.color}25`, color: s.color }}
          >
            <Icon size={48} weight="duotone" />
          </div>
          <p className="text-xl font-semibold mb-1" style={{ color: s.color }}>
            {s.label}
          </p>
          {order.token_number != null && (
            <p className="text-5xl font-extrabold mt-3 tracking-tight">
              <span className="text-white/40 text-xl mr-1">Token</span>
              {order.token_number}
            </p>
          )}
        </div>

        {/* Items list */}
        {order.items && order.items.length > 0 && (
          <div className="rounded-2xl bg-white/5 border border-white/10 p-4 mb-4">
            <p className="text-white/50 text-xs uppercase tracking-widest mb-3 flex items-center gap-2">
              <ForkKnife size={14} /> Items
            </p>
            <div className="space-y-2">
              {order.items.map((it, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span>
                    <span className="text-white/60 mr-2">{it.quantity}×</span>
                    {it.name}
                  </span>
                  <span className="text-white/40 text-xs capitalize">{it.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Total */}
        {order.total != null && (
          <div className="rounded-2xl bg-white/5 border border-white/10 p-4 flex items-center justify-between">
            <span className="text-white/60 text-sm flex items-center gap-2">
              <Receipt size={14} /> Total
            </span>
            <span className="text-2xl font-bold">${(order.total || 0).toFixed(2)}</span>
          </div>
        )}

        <p className="text-center text-white/30 text-xs mt-6">
          Auto-refreshes every few seconds. Bookmark this page to check back later.
        </p>
      </div>
    </div>
  );
};

export default OrderStatusPage;
